import { getMyAccountSections } from './myAccountSections';
import { frase } from '../frase';

/**
 * LE VOCI DELLA BARRA IN ALTO.
 *
 * Non ricalcola niente: prende le voci che il menu del conto gia' calcola
 * con getMyAccountSections() - una per ogni voce del catalogo che sta nel
 * gettone, vedi utils/permessi.js - e le ridistribuisce nei tre gruppi
 * della striscia. Il filtro delle voci resta in un posto solo: se domani
 * cambia una regola, cambia li' e la barra la segue.
 *
 * Le voci che spariscono, e perche':
 *  - Commissioni: il meccanismo non esiste. Tornera' quando ci sara'.
 *  - Quote: e' del backoffice, non del cliente.
 *  - Nuovo Utente: non e' una voce, e' un'azione. E' gia' un bottone dentro
 *    Lista utenti (che e' dove si vedono le cose su cui agisce); come voce di
 *    menu apriva la stessa pagina con la finestra sopra.
 *  - Pannello di controllo: era il menu ridisegnato a bottoni. Un menu solo.
 */

/*
 * LE VOCI DELLA STRISCIA.
 *
 * Non sono copiate dal menu del frontend vecchio: sono quelle decise dopo
 * aver verificato nel backoffice e nei servizi cosa esiste. Accanto a
 * ognuna c'e' scritto cosa c'e' dietro, cosi' non serve andarlo a cercare.
 *
 * Alcune voci ci sono ma dietro non hanno ancora niente: sono una scelta,
 * non una dimenticanza. Restano perche' il meccanismo verra' fatto, e la
 * pagina dice che non e' ancora costruita invece di far finta.
 *
 * Fuori del tutto, e perche':
 *  - PIN e antieffrazione: di tutti e due non esiste una riga di codice in
 *    nessun servizio. Niente conteggio dei tentativi falliti, niente blocco
 *    automatico.
 *  - Commissioni: il meccanismo non esiste da nessuna parte.
 *  - Quote: e' del backoffice, non del cliente.
 *  - Nuovo Utente: non e' una voce, e' un'azione. E' gia' un bottone dentro
 *    Lista utenti, cioe' dove si vedono le cose su cui agisce.
 *
 * La chiave e' il percorso: le etichette cambiano, i percorsi no.
 */
/* Le etichette passano da frase(): sono scritte qui, in testa al file, e
   si traducono nella barra con t(voce.etichetta), nella lingua di chi
   guarda in quel momento (vedi src/frase.js). */
const DOVE_VA = {
  // POST /v1/ticket-service/admin/ticket/search
  '/my-account/lista-scommesse': { gruppo: 'conto', ordine: 1, etichetta: frase('Lista scommesse') },
  // GET /private/credit/{userId}/movements
  '/my-account/lista-movimenti': { gruppo: 'conto', ordine: 2, etichetta: frase('Lista movimenti') },
  // GET e PUT /private/profile/{userId}
  '/my-account/profilo':         { gruppo: 'conto', ordine: 3, etichetta: frase('Il mio profilo'), staccato: true },
  // POST /api/password/change + /api/password/confirm
  '/my-account/sicurezza':       { gruppo: 'conto', ordine: 4, etichetta: frase('Sicurezza') },

  // La Cassa (payment-service, NOWPayments): versare e prelevare in
  // criptovaluta. Versamento e Prelievo portano li'.
  '/my-account/versamento': { gruppo: 'pagamenti', ordine: 1, etichetta: frase('Versamento') },
  '/my-account/prelievo':   { gruppo: 'pagamenti', ordine: 2, etichetta: frase('Prelievo') },

  // GET /api/messaggi/miei (notification-service): avvisi e promozioni
  '/my-account/messaggi':            { gruppo: 'messaggi', ordine: 1, etichetta: frase('Messaggi') },
  // GET e PUT /api/messaggi/miei/preferenze: le promozioni per email e Telegram
  '/my-account/messaggi/preferenze': { gruppo: 'messaggi', ordine: 2, etichetta: frase('Preferenze') },

  // GET /admin/users, GET /private/jurisdiction/all, POST /private/credit/figures
  '/my-account/lista-utenti': { gruppo: 'rete', ordine: 1, etichetta: frase('Lista utenti') },
  // GET /private/credit/{userId}/figures per le cifre. Il movimento vero e'
  // POST /private/credit/transfer, che esiste: il credito gira fra chi e'
  // entrato e un nodo della sua rete, come nel backoffice.
  '/my-account/giroconto':    { gruppo: 'rete', ordine: 2, etichetta: frase('Giroconto') },
  // DA COLLEGARE. Nessun controller "report" in nessun servizio: il sistema
  // verra' fatto. La cosa piu' vicina che gia' gira e' il registro audit
  // (POST /private/audit/search).
  '/my-account/report':       { gruppo: 'rete', ordine: 3, etichetta: frase('Report'), staccato: true },
};

// Le voci della striscia, nell'ordine in cui stanno. Il giroconto sta nella
// rete e non in una cassa tutta sua: e' un movimento fra chi e' entrato e un
// nodo della sua rete, ed e' li' che il backoffice lo tiene.
const GRUPPI = [
  { chiave: 'conto',     etichetta: frase('Il mio conto') },
  { chiave: 'pagamenti', etichetta: frase('Pagamenti') },
  { chiave: 'messaggi',  etichetta: frase('Messaggistica') },
  { chiave: 'rete',      etichetta: frase('Rete') },
];

export function vociDellaBarra() {
  const sezioni = getMyAccountSections() || [];

  // Tutte le voci concesse, appiattite, senza doppioni di percorso.
  const concesse = new Map();
  sezioni.forEach((sez) => {
    (sez.items || []).forEach((voce) => {
      if (!voce || !voce.path) return;
      if (voce.openCreateModal) return;            // Nuovo Utente: e' un bottone
      if (!concesse.has(voce.path)) concesse.set(voce.path, voce);
    });
  });

  const perGruppo = {};
  concesse.forEach((voce, percorso) => {
    const posto = DOVE_VA[percorso];
    if (!posto) return;                            // non sta nella striscia: fuori
    (perGruppo[posto.gruppo] = perGruppo[posto.gruppo] || []).push({
      etichetta: posto.etichetta,
      percorso,
      nota: posto.nota,
      staccato: !!posto.staccato,
      ordine: posto.ordine,
    });
  });

  return GRUPPI
      .map((g) => {
        const voci = (perGruppo[g.chiave] || []).sort((a, b) => a.ordine - b.ordine);
        // La prima voce e' quella predefinita: da scrivania, cliccare il nome
        // del gruppo ci porta dritto senza aprire la tendina.
        if (voci.length) voci[0].predefinita = true;
        return { ...g, voci };
      })
      // Un gruppo senza nessuna voce concessa non compare. Un giocatore non
      // ha nessuno sotto di se': la voce Rete non gli si mostra vuota.
      .filter((g) => g.voci.length > 0);
}

/** Il gruppo in cui si sta navigando adesso, per accendere la voce giusta. */
export function gruppoDelPercorso(gruppi, percorso) {
  const trovato = gruppi.find((g) => g.voci.some((v) => v.percorso === percorso));
  return trovato ? trovato.chiave : null;
}

export default vociDellaBarra;
